// SecureSpot.jsx — "Secure your spot" (owner 2026-11-04)
//
// Sophia books without asking for a card and tells the client "two days
// before, our team will call you to secure your spot with a card". Frances
// works the Secure Calls list: every appointment in the next two days that
// isn't secured yet. Per appointment:
//   • 🔒 Secured — automatic when a card is on file, or toggled by staff;
//   • 📲 Text secure link — one tap, while she's still on the phone;
//   • 🚫 Won't give a card — the appointment stays booked but is NOT secured:
//     Sophia offers that spot to the waitlist and whoever says YES first
//     takes it (the client gets a kind text). Can be undone.
import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabase";

const C = {
  gold:"#b48c3c", goldD:"#8a6f2e", goldBg:"rgba(180,140,60,0.08)", borderGold:"rgba(180,140,60,0.3)",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)", red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af",
  light:"#f3f4f6", border:"#e8e4df", surface:"#ffffff", warm:"#b45309", warmL:"rgba(245,158,11,0.12)",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const nyYmd = (d = new Date()) => d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};
const fmtDay = (d) => {
  if (!d) return "";
  if (d === nyYmd()) return "Today";
  if (d === nyYmd(new Date(Date.now() + 86400000))) return "Tomorrow";
  return new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const fmtPhone = (raw) => {
  const d = String(raw || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : raw || "";
};
const cleanTreat = (t) => String(t || "appointment").replace(/^\$[\d.,]+(\/unit)?\s*/i, "");

async function staffName() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return "Staff";
    const { data: p } = await supabase.from("profiles").select("full_name").eq("id", user.id).maybeSingle();
    return p?.full_name || user.email || "Staff";
  } catch { return "Staff"; }
}

// Card on file for these client ids → Set of ids.
export async function clientsWithCard(cids) {
  const ids = [...new Set((cids || []).filter(Boolean))];
  if (!ids.length) return new Set();
  const [{ data: cl }, { data: pm }] = await Promise.all([
    supabase.from("clients").select("id,card_on_file").in("id", ids),
    supabase.from("saved_payment_methods").select("client_id").in("client_id", ids),
  ]);
  const out = new Set((pm || []).map((r) => r.client_id));
  for (const c of cl || []) if (c.card_on_file) out.add(c.id);
  return out;
}

// Secured / not secured / declined, for one appointment.
export function secureState(appt, hasCard) {
  if (hasCard) return "card";
  if (appt?.card_requirement_waived) return "waived";
  if (appt?.card_status === "secured") return "secured";
  if (appt?.card_status === "declined") return "declined";
  return "open";
}

export async function sendSecureLink(appt, client) {
  const phone = client?.phone || "";
  if (!phone) { alert("No phone number on file for this client."); return false; }
  const first = String(client?.name || appt.cn || "there").trim().split(/\s+/)[0];
  const link = `https://www.elyonhq.com/card?cid=${appt.cid}&n=${encodeURIComponent(first)}`;
  const msg = `Hi ${first}, it's Nova Med Spa 💛 To secure your ${cleanTreat(appt.treat)} on ${fmtDay(appt.date)} at ${fmt12(appt.time)}, please save a card here. You won't be charged today. Free to cancel or move up to 24 hours before; changes within 24h are $25 (credited to your treatment), no-shows $50. Secure link: ${link}`;
  const { data, error } = await supabase.functions.invoke("send-sms", {
    body: { to: phone, message: msg, sender: "system", source: "secure_spot_link" },
  });
  if (error || data?.ok === false) { alert("Text failed: " + (error?.message || data?.error || data?.skipped || "unknown")); return false; }
  await supabase.from("appointments").update({ card_link_sent_at: new Date().toISOString() }).eq("id", appt.id);
  return true;
}

async function setCardStatus(appt, status) {
  const by = await staffName();
  const { error } = await supabase.from("appointments").update({
    card_status: status, card_status_at: status ? new Date().toISOString() : null, card_status_by: status ? by : null,
  }).eq("id", appt.id);
  if (error) { alert("Save failed: " + error.message); return false; }
  return true;
}

const pill = (bg, color, border) => ({
  padding: "6px 11px", background: bg, color, border: `1px solid ${border || color + "40"}`, borderRadius: 8,
  fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: F, whiteSpace: "nowrap",
});

// The bar inside an appointment's details: status + the three actions.
export function SecureSpotBar({ appt, compact = false, onChanged }) {
  const [hasCard, setHasCard] = useState(false);
  const [client, setClient] = useState(null);
  const [row, setRow] = useState(appt);
  const [busy, setBusy] = useState("");
  useEffect(() => { setRow(appt); }, [appt]);
  useEffect(() => {
    let off = false;
    if (!appt?.cid) return;
    (async () => {
      const [cards, { data: c }] = await Promise.all([
        clientsWithCard([appt.cid]),
        supabase.from("clients").select("name,phone").eq("id", appt.cid).maybeSingle(),
      ]);
      if (off) return;
      setHasCard(cards.has(appt.cid)); setClient(c || null);
    })();
    return () => { off = true; };
  }, [appt?.cid]);
  if (!appt || ["cancelled", "no_show", "completed"].includes(appt.status)) return null;
  const st = secureState(row, hasCard);
  const look = {
    card: ["🔒 Secured · card on file", C.green, C.greenL],
    waived: ["Card waived", C.muted, C.light],
    secured: ["🔒 Secured", C.green, C.greenL],
    declined: ["🚫 Not secured · she won't give a card · Sophia is offering this spot to the waitlist", C.red, C.redL],
    open: ["⚠ Not secured yet · no card", C.warm, C.warmL],
  }[st];
  const act = async (what) => {
    if (busy) return;
    setBusy(what);
    try {
      let patch = null;
      if (what === "link") {
        if (await sendSecureLink(row, client)) { patch = { card_link_sent_at: new Date().toISOString() }; }
      } else if (what === "secured") {
        const next = row.card_status === "secured" ? null : "secured";
        if (await setCardStatus(row, next)) patch = { card_status: next };
      } else if (what === "declined") {
        if (row.card_status !== "declined" && !window.confirm(`${row.cn} won't give a card?\n\nHer appointment stays booked, but it is NOT secured. Sophia will offer this spot to the waitlist, and if someone says YES first, they get it (she'll get a kind text).`)) { setBusy(""); return; }
        const next = row.card_status === "declined" ? null : "declined";
        if (await setCardStatus(row, next)) patch = { card_status: next };
      }
      if (patch) { const n = { ...row, ...patch }; setRow(n); if (onChanged) onChanged(n); }
    } finally { setBusy(""); }
  };
  return (
    <div style={{ padding: compact ? "8px 10px" : "12px 14px", background: look[2], border: `1px solid ${look[1]}35`, borderRadius: 10, marginBottom: compact ? 0 : 16 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: look[1], marginBottom: st === "card" || st === "waived" ? 0 : 8, fontFamily: F }}>
        {look[0]}
        {row.card_link_sent_at && st !== "card" && <span style={{ fontWeight: 600, color: C.muted, marginLeft: 8, fontSize: 11 }}>· link texted {fmtDay(row.card_link_sent_at.slice(0, 10))}</span>}
      </div>
      {st !== "card" && st !== "waived" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => act("link")} disabled={!!busy} style={pill(C.goldBg, C.goldD, C.borderGold)}>{busy === "link" ? "Sending…" : "📲 Text secure link"}</button>
          <button onClick={() => act("secured")} disabled={!!busy} style={pill(row.card_status === "secured" ? C.green : C.greenL, row.card_status === "secured" ? "#fff" : C.green)}>{row.card_status === "secured" ? "✅ Card taken (undo)" : "✅ Card taken"}</button>
          <button onClick={() => act("declined")} disabled={!!busy} style={pill(row.card_status === "declined" ? C.red : C.redL, row.card_status === "declined" ? "#fff" : C.red)}>{row.card_status === "declined" ? "🚫 Won't give a card (undo)" : "🚫 Won't give a card"}</button>
        </div>
      )}
    </div>
  );
}

// Live count for the calendar header button.
export function useSecureCallCount() {
  const [n, setN] = useState(0);
  const load = useCallback(async () => {
    const rows = await loadSecureList();
    setN(rows.filter((r) => r._state === "open").length);
  }, []);
  useEffect(() => {
    load();
    const ch = supabase.channel("secure-count")
      .on("postgres_changes", { event: "*", schema: "public", table: "appointments" }, () => load())
      .subscribe();
    const t = setInterval(load, 5 * 60000);
    return () => { supabase.removeChannel(ch); clearInterval(t); };
  }, [load]);
  return n;
}

// Appointments tomorrow and the day after (two days before = the call window).
async function loadSecureList() {
  const from = nyYmd(new Date(Date.now() + 86400000)), to = nyYmd(new Date(Date.now() + 2 * 86400000));
  const { data } = await supabase.from("appointments")
    .select("id,cid,cn,treat,prov,date,time,status,card_status,card_status_by,card_link_sent_at,card_requirement_waived,secure_call_attempts,secure_last_call_at")
    .gte("date", from).lte("date", to).not("status", "in", "(cancelled,no_show,completed)")
    .order("date").order("time");
  const rows = data || [];
  const cards = await clientsWithCard(rows.map((r) => r.cid));
  return rows.map((r) => ({ ...r, _state: secureState(r, cards.has(r.cid)) }));
}

export function SecureCallsDrawer({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [phones, setPhones] = useState({});
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    const r = await loadSecureList();
    const ids = [...new Set(r.map((x) => x.cid).filter(Boolean))];
    const { data: cl } = ids.length ? await supabase.from("clients").select("id,phone").in("id", ids) : { data: [] };
    setPhones(Object.fromEntries((cl || []).map((c) => [c.id, c.phone])));
    setRows(r); setLoading(false);
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;
  const noAnswer = async (r) => {
    await supabase.from("appointments").update({ secure_call_attempts: (r.secure_call_attempts || 0) + 1, secure_last_call_at: new Date().toISOString() }).eq("id", r.id);
    load();
  };
  const toCall = rows.filter((r) => r._state === "open");
  const declined = rows.filter((r) => r._state === "declined");
  const done = rows.filter((r) => ["card", "secured", "waived"].includes(r._state));
  const Card = ({ r }) => (
    <div style={{ padding: "12px 14px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{r.cn}</div>
          <div style={{ fontSize: 12, color: C.muted }}>{cleanTreat(r.treat)}{r.prov ? ` · ${String(r.prov).split(" ")[0]}` : ""}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: C.gold }}>{fmtDay(r.date)}</div>
          <div style={{ fontSize: 12, color: C.text }}>{fmt12(r.time)}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8, fontSize: 12 }}>
        {phones[r.cid] && <a href={`tel:${String(phones[r.cid]).replace(/\D/g, "")}`} style={{ color: C.gold, fontWeight: 700, textDecoration: "none" }}>📞 {fmtPhone(phones[r.cid])}</a>}
        {r._state === "open" && <button onClick={() => noAnswer(r)} style={pill(C.light, C.muted, C.border)}>No answer</button>}
        {(r.secure_call_attempts || 0) > 0 && <span style={{ color: C.dim, fontSize: 11 }}>called {r.secure_call_attempts}× · last {new Date(r.secure_last_call_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>}
      </div>
      <SecureSpotBar appt={r} compact onChanged={() => load()} />
    </div>
  );
  const Section = ({ title, list, note }) => list.length ? (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", margin: "4px 2px 8px" }}>{title} · {list.length}</div>
      {note && <div style={{ fontSize: 11, color: C.muted, margin: "-4px 2px 8px" }}>{note}</div>}
      {list.map((r) => <Card key={r.id} r={r} />)}
    </div>
  ) : null;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1100, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(460px, 100vw)", background: "#f8f5f0", zIndex: 1101, boxShadow: "-12px 0 32px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 20px", borderBottom: `1px solid ${C.border}`, background: `linear-gradient(135deg, ${C.goldBg}, #fff)` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.gold, fontFamily: S }}>🔒 Secure Calls</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={load} style={pill(C.surface, C.muted, C.border)}>↻</button>
              <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: C.muted, padding: 0, lineHeight: 1 }}>×</button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>Tomorrow and the day after. Call, take the card ("to secure your spot"), or text the secure link while she's on the phone.</div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
          {loading && <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 12 }}>Loading…</div>}
          {!loading && !toCall.length && !declined.length && (
            <div style={{ padding: "40px 20px", textAlign: "center", color: C.muted, fontSize: 13 }}>🎉 Everyone in the next two days is secured.</div>
          )}
          {!loading && <>
            <Section title="📞 To call" list={toCall} />
            <Section title="🚫 Won't give a card" list={declined} note="Still booked, not secured. Sophia offers these spots to the waitlist; first YES takes it." />
            <Section title="🔒 Secured" list={done} />
          </>}
        </div>
      </div>
    </>
  );
}
